import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ThemeService } from '../../service/theme.service';
import { InvoiceGenerationStateService } from '../../service/invoice-generation-state.service';
import { ClientService } from '../../service/client.service';
import { IotService } from '../../service/iot.service';
import { BinsService } from '../../service/bins.service';
import { TranslateService } from '@ngx-translate/core';
import { PdfService } from '../../service/pdf.service';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeeInputService } from '../../service/fee-input.service';

@Component({
  selector: 'app-subscription-invoice',
  templateUrl: './subscription-invoice.component.html',
  styleUrls: ['./subscription-invoice.component.css'],
  standalone: false,
})
export class SubscriptionInvoiceComponent implements OnInit, OnDestroy {
  // Client context for invoice generation.
  clientId = '';
  clientName = '';
  clientAddress = '';
  // Selected device count used for per-device pricing.
  deviceCount = 0;
  // Editable fee inputs (preloaded from template).
  subscriptionFee = '';
  serviceFee = '';
  // Subscription billing period.
  periodStart = '';
  periodEnd = '';
  // Loading state for async template/device data.
  loading = false;
  // Loading state for device details.
  loadingDetails = false;
  // Selected device rows used for modal and PDF.
  selectedDeviceDetails: Array<{
    thingName: string;
    address: string;
    usageKey: string;
    usageLabel: string;
  }> = [];
  private destroy$ = new Subject<void>();
  private lastStateKey = '';

  constructor(
    public theme: ThemeService,
    private invoiceState: InvoiceGenerationStateService,
    private clientService: ClientService,
    private iotService: IotService,
    public binService: BinsService,
    private translate: TranslateService,
    private pdf: PdfService,
    private router: Router,
    private http: HttpClient,
    public feeInput: FeeInputService
  ) {}

  ngOnInit(): void {
    // React to workflow state changes so filters stay dynamic.
    this.invoiceState.state$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => this.applyState(state.clientId, state.devices));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyState(clientId: string, devices: string[]): void {
    if (!clientId || devices.length === 0) {
      this.router.navigate(['/invoice-generate']);
      return;
    }

    const nextKey = `${clientId}::${devices.join('|')}`;
    if (nextKey === this.lastStateKey) {
      return;
    }
    this.lastStateKey = nextKey;

    this.clientId = clientId;
    this.deviceCount = devices.length;
    this.loadInvoiceTemplate(clientId);
    this.loadClientName(clientId);
    this.loadSelectedDeviceDetails();
    // Default to current month when the workflow changes.
    this.setDefaultPeriod();
  }

  get isBusy(): boolean {
    return this.loading || this.loadingDetails;
  }

  get canGenerate(): boolean {
    return !this.isBusy && this.selectedDeviceDetails.length > 0;
  }

  private setDefaultPeriod(): void {
    // Default period = first and last day of current month.
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    this.periodStart = start.toISOString().slice(0, 10);
    this.periodEnd = end.toISOString().slice(0, 10);
  }

  private async loadInvoiceTemplate(clientId: string): Promise<void> {
    // Fetch subscription/service fees from template API.
    this.loading = true;
    try {
      const url =
        environment.api.stage +
        environment.api.route.getClientInvoiceTemplate +
        '&client_id=' +
        clientId;
      const response = await firstValueFrom(this.http.get<any>(url));
      const template = Array.isArray(response) ? response[0] : response;
      if (template) {
        this.subscriptionFee = this.formatFeeValue(template.subscription_fee);
        this.serviceFee = this.formatFeeValue(template.service_fee);
      }
    } catch (error) {
      console.error('[SubscriptionInvoice] Failed to load template', error);
      this.subscriptionFee = '';
      this.serviceFee = '';
    } finally {
      this.loading = false;
    }
  }

  private formatFeeValue(value: number | string | null | undefined): string {
    // Normalize fee display to 2 decimals.
    if (value === null || value === undefined || value === '') return '';
    const numericValue = typeof value === 'string' ? value : String(value);
    return this.feeInput.formatFeeValue(numericValue);
  }

  private async loadClientName(clientId: string): Promise<void> {
    // Resolve display name + address for tax calculation.
    try {
      await this.clientService.getClientsForSelect();
      const clients = this.clientService.clientsArray || [];
      const match = clients.find((c: any) => c.client_id === clientId);
      this.clientName = match?.legal_name || match?.client_name || clientId;
      this.clientAddress = match?.address || '';
    } catch (error) {
      console.error('[SubscriptionInvoice] Failed to load client name', error);
      this.clientName = clientId;
      this.clientAddress = '';
    }
  }

  private async loadSelectedDeviceDetails(): Promise<void> {
    // Build device rows for modal/PDF.
    if (!this.invoiceState.devices.length) {
      this.selectedDeviceDetails = [];
      return;
    }

    this.loadingDetails = true;
    try {
      try {
        if (!this.iotService.devicesArray || this.iotService.devicesArray.length === 0) {
          await this.iotService.adminDeviceArray();
        }
      } catch (error) {
        console.error('[SubscriptionInvoice] Failed to load devices', error);
      }

      const deviceSet = new Set(this.invoiceState.devices);
      const devices = (this.iotService.devicesArray || []).filter((row: any) =>
        deviceSet.has(row.thingName)
      );

      this.selectedDeviceDetails = devices.map((row: any) => {
        const usageKey = this.binService.getBinUsage(row.bin?.bin_usage ?? '');
        return {
          thingName: row.thingName,
          address: row.bin?.bin_address || this.translate.instant('withoutBin'),
          usageKey,
          usageLabel: usageKey ? this.translate.instant(usageKey) : '',
        };
      });
    } finally {
      this.loadingDetails = false;
    }
  }

  async generateInvoicePdf(): Promise<void> {
    // Generate subscription invoice PDF with period and taxes.
    const subscriptionFee = parseFloat(this.subscriptionFee || '0') || 0;
    const serviceFee = parseFloat(this.serviceFee || '0') || 0;

    await this.pdf.generateSubscriptionInvoicePdf({
      clientName: this.clientName || this.clientId,
      clientId: this.clientId,
      clientAddress: this.clientAddress,
      devices: this.selectedDeviceDetails.map((row) => ({
        thingName: row.thingName,
        address: row.address,
        usage: row.usageLabel || row.usageKey,
      })),
      subscriptionFee,
      serviceFee,
      periodStart: this.periodStart,
      periodEnd: this.periodEnd,
    });
  }
}
